import React, { useMemo, SyntheticEvent } from "react";

import { Autocomplete, Checkbox, FormControlLabel, Stack, TextField } from "@mui/material";
import { CharacteristicsTableItem, IStatus } from "store/types";

import { getAutocompleteValue, PlainDataFields, Values } from "./Form";
import { useDealStatusesSelector } from "store";

const trueFalseOptions = [
  { id: 0, name: "true" },
  { id: 1, name: "false" }
];

const reduceMethodOption = [
  { id: 0, name: "last" },
  { id: 1, name: "sum" }
];

const postfixOption = [
  { id: 0, name: "%" },
  { id: 1, name: "$" }
];

const numerifyOption = [
  { id: 0, name: "0,0" },
  { id: 1, name: "00,00" }
];

interface Props {
  values: Values;
  errors: Partial<Record<keyof Values, string[]>>;
  updatePlainData: (field: PlainDataFields, value: CharacteristicsTableItem[PlainDataFields]) => void;
}

export const GlobalTab: React.FC<Props> = ({ values, errors, updatePlainData }) => {
  const dealStatuses = useDealStatusesSelector();

  // Derive checkbox state from values - no duplicate state needed
  const isRequiredEnabled = values.required_to_status_ids !== null;

  const selectedStatuses = useMemo(() => {
    if (!values.required_to_status_ids) return [];
    return dealStatuses.filter((status) => values.required_to_status_ids?.includes(status.id));
  }, [values.required_to_status_ids, dealStatuses]);

  const handleRequiredToggle = (checked: boolean) => {
    updatePlainData("required_to_status_ids", checked ? [] : null);
  };

  const handleStatusChange = (_: SyntheticEvent, newValue: IStatus[]) => {
    updatePlainData(
      "required_to_status_ids",
      newValue.map((status) => status.id)
    );
  };
  return (
    <Stack sx={{ gap: "24px", flexGrow: 1, alignItems: "stretch" }}>
      <Autocomplete
        getOptionLabel={(option: { id: number; name: string }) => option.name}
        size="small"
        options={trueFalseOptions}
        value={getAutocompleteValue(values?.is_global?.toString(), trueFalseOptions)}
        disableClearable
        onChange={(_, value) => updatePlainData("is_global", value.name === "true")}
        disabled={values.is_locked_to_edit}
        renderInput={(params) => (
          <TextField
            {...params}
            error={!!errors.is_global}
            helperText={errors.is_global}
            value={values.is_global}
            variant="outlined"
            size="small"
            label="Is Global"
            disabled={values.is_locked_to_edit}
          />
        )}
      />
      <Autocomplete
        getOptionLabel={(option: { id: number; name: string }) => option.name}
        size="small"
        options={trueFalseOptions.filter((option) => option.name !== values.is_show.toString())}
        value={getAutocompleteValue(values?.is_show?.toString(), trueFalseOptions)}
        disableClearable
        onChange={(_, value) => updatePlainData("is_show", value.name === "true")}
        disabled={values.is_locked_to_edit}
        renderInput={(params) => (
          <TextField
            {...params}
            error={!!errors.is_show}
            helperText={errors.is_show}
            value={values.is_show}
            variant="outlined"
            size="small"
            label="Show"
            disabled={values.is_locked_to_edit}
          />
        )}
      />
      <Autocomplete
        getOptionLabel={(option: { id: number; name: string }) => option.name}
        size="small"
        options={trueFalseOptions.filter((option) => option.name !== values.is_multiple.toString())}
        value={getAutocompleteValue(values?.is_multiple?.toString(), trueFalseOptions)}
        disableClearable
        onChange={(_, value) => updatePlainData("is_multiple", value.name === "true")}
        disabled={values.is_locked_to_edit}
        renderInput={(params) => (
          <TextField
            {...params}
            error={!!errors.is_multiple}
            helperText={errors.is_multiple}
            value={values.is_multiple}
            variant="outlined"
            size="small"
            label="Multiple"
            disabled={values.is_locked_to_edit}
          />
        )}
      />
      <Autocomplete
        getOptionLabel={(option: { id: number; name: string }) => option.name}
        size="small"
        options={numerifyOption}
        clearText=""
        value={getAutocompleteValue(values?.numerify, numerifyOption)}
        onChange={(_, value) => updatePlainData("numerify", value?.name)}
        disabled={values.is_locked_to_edit}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            label="Numerify"
            value={values?.numerify}
            error={!!errors.numerify}
            helperText={errors.numerify}
            disabled={values.is_locked_to_edit}
          />
        )}
      />
      <Autocomplete
        getOptionLabel={(option: { id: number; name: string }) => option.name}
        size="small"
        options={postfixOption}
        clearText=""
        value={getAutocompleteValue(values?.postfix, postfixOption)}
        onChange={(_, value) => updatePlainData("postfix", value?.name)}
        disabled={values.is_locked_to_edit}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            label="Postfix"
            value={values?.postfix}
            error={!!errors.postfix}
            helperText={errors.postfix}
            disabled={values.is_locked_to_edit}
          />
        )}
      />
      <Autocomplete
        getOptionLabel={(option: { id: number; name: string }) => option.name}
        size="small"
        options={reduceMethodOption}
        value={getAutocompleteValue(values?.reduce_method, reduceMethodOption)}
        clearText=""
        onChange={(_, value) => updatePlainData("reduce_method", value?.name)}
        disabled={values.is_locked_to_edit}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            label="Reduce Method"
            value={values?.reduce_method}
            error={!!errors.reduce_method}
            helperText={errors.reduce_method}
            disabled={values.is_locked_to_edit}
          />
        )}
      />
      <Stack direction="row" alignItems="center" gap="10px" ml="-11px">
        <FormControlLabel
          control={
            <Checkbox
              checked={isRequiredEnabled}
              onChange={(e) => handleRequiredToggle(e.target.checked)}
              disabled={values.is_locked_to_edit}
            />
          }
          label="Is required to status:"
          sx={{ margin: 0 }}
        />
        <Autocomplete
          multiple
          getOptionLabel={(option: IStatus) => option.name}
          size="small"
          options={dealStatuses}
          value={selectedStatuses}
          onChange={handleStatusChange}
          disabled={!isRequiredEnabled || values.is_locked_to_edit}
          sx={{ flex: 1, minWidth: 200 }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder="Select"
            />
          )}
        />
      </Stack>
    </Stack>
  );
};
